import {Bindings} from "data/constants/bindings";
import type {IPickResponse, IPinpointApiProvider} from "data/providers/api/pinpoint.api.provider";
import {inject, injectable} from "inversify";
import {findIndex, isEqual} from "lodash";
import {action, makeAutoObservable, observable, runInAction} from "mobx";

export interface IPinpointStore {
	get myPicks(): IPickResponse[];
	get isPickChanged(): boolean;

	getPickByRoundId(roundId: number): IPickResponse | null;
	isLongestDriveFilled(roundId: number): boolean;
	isNearestPinFilled(roundId: number): boolean;
	fetchMyPick(roundId: number): Promise<void>;
	fetchListOfMyPicks(): Promise<void>;
	updatePickByRoundId(roundId: number, pick: Partial<IPickResponse>): void;
	submitPick(pick: Partial<IPickResponse>): Promise<void>;
}

@injectable()
export class PinpointStore implements IPinpointStore {
	@observable private _myPicks: IPickResponse[] = [];
	@observable private _isPickChanged = true;

	constructor(
		@inject(Bindings.PinpointApiProvider) private _pinpointApiProvider: IPinpointApiProvider
	) {
		makeAutoObservable(this);
	}

	get myPicks() {
		return this._myPicks;
	}

	get isPickChanged() {
		return this._isPickChanged;
	}

	getPickByRoundId(roundId: number) {
		return this._myPicks.find((pick) => isEqual(pick.roundId, roundId)) || null;
	}

	isLongestDriveFilled(roundId: number) {
		const pick = this.getPickByRoundId(roundId);
		return Boolean(pick?.longestDriveLineup?.value && pick?.longestDriveLineup.golfers.length);
	}

	isNearestPinFilled(roundId: number) {
		const pick = this.getPickByRoundId(roundId);
		return Boolean(pick?.nearestPinLineup?.value && pick?.nearestPinLineup.golfers.length);
	}

	@action updatePickByRoundId(roundId: number, pick: Partial<IPickResponse>) {
		const found = this._myPicks.find((pick) => isEqual(pick.roundId, roundId));

		if (found) {
			Object.assign(found, pick);
		} else {
			this._myPicks.push(pick as IPickResponse);
		}

		runInAction(() => {
			this._isPickChanged = true;
		});
	}

	@action async fetchMyPick(roundId: number) {
		const result = await this._pinpointApiProvider.getMyPick({roundId});
		const {pick} = result.data.success;

		runInAction(() => {
			const index = findIndex(this._myPicks, {id: pick.id});

			if (index !== -1) {
				this._myPicks[index] = pick;
			} else {
				this._myPicks.push(pick);
			}

			this._isPickChanged = false;
		});
	}

	@action async fetchListOfMyPicks() {
		const result = await this._pinpointApiProvider.getListOfMyPicks();
		const {picks} = result.data.success;

		runInAction(() => {
			this._myPicks = picks;
			this._isPickChanged = false;
		});
	}

	@action async submitPick(pick: Partial<IPickResponse>) {
		const result = await this._pinpointApiProvider.updatePick({
			roundId: pick.roundId!,
			longestDriveLineup: pick.longestDriveLineup!,
			nearestPinLineup: pick.nearestPinLineup!,
		});

		const {pick: pickResult} = result.data.success;

		runInAction(() => {
			const index = findIndex(this._myPicks, {id: pick.id});

			if (index !== -1) {
				this._myPicks[index] = pickResult;
			} else {
				this._myPicks.push(pickResult);
			}

			this._isPickChanged = false;
		});
	}
}
